// StaffHeader: Professional header for staff panel
import React, { useEffect, useState, useRef, useContext } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Badge, Tooltip, InputBase } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SearchIcon from '@mui/icons-material/Search';
import NotificationDropdown from './NotificationDropdown';
import ProfileDropdown from './ProfileDropdown';
import ThemeToggle from './ThemeToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import { SettingsContext } from '../context/SettingsContext';
import api from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import { useProfile } from '../context/ProfileContext';

function StaffHeader() {
  const settings = useContext(SettingsContext);
  const { profile } = useProfile() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [assignedSummary, setAssignedSummary] = useState({ assignedTotal: 0, assignedActive: 0, assignedPending: 0 });
  const [backoffUntil, setBackoffUntil] = useState(0);
  const [unread, setUnread] = useState(0);
  const { notifications, connected } = useNotifications();
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/,'');
  const logoSrc = settings?.logoUrl || `${API_BASE}/api/public/logo`;
  const platformName = settings?.platformName || 'Expo & Conference Platform';
  const displayName = profile?.name || '';
  const mountedRef = useRef(false);
  const intervalRef = useRef(null);

  const routeTitle = (path) => {
    if (!path) return 'Staff';
    if (path === '/staff') return 'Dashboard';
    if (path.startsWith('/staff/bookings')) return 'Bookings';
    if (path.startsWith('/staff/tickets')) return 'Tickets';
    if (path.startsWith('/staff/cms')) return 'CMS';
    if (path.startsWith('/staff/notifications')) return 'Notifications';
    if (path.startsWith('/staff/settings')) return 'Settings';
    return '';
  };

  const fetchAssignedSummary = async () => {
    if (Date.now() < backoffUntil) return; // respect backoff when server rate-limits
    try {
      const res = await api.get('/api/bookings/summary/assigned');
      if (!mountedRef.current) return;
      if (res?.data) setAssignedSummary(res.data);
    } catch (err) {
      const status = err?.response?.status;
      console.warn('Failed to load assigned summary', err?.response?.data || err.message);
      if (status === 429) {
        // Received Too Many Requests — back off polling for a minute
        setBackoffUntil(Date.now() + 60_000);
      }
    }
  };

  const fetchUnread = async () => {
    try {
      const res = await api.get('/api/notifications/unread/count');
      if (res?.data?.count != null) return setUnread(res.data.count);
    } catch (e) {
      // fallback to dashboard summary when unread endpoint is not available
    }
    try {
      const d = await api.get('/api/dashboard');
      if (d?.data?.notifications && typeof d.data.notifications.pending === 'number') {
        setUnread(d.data.notifications.pending);
      }
    } catch (err) {
      // ignore final failures
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchAssignedSummary();
    fetchUnread();
    // If SSE notifications are connected, poll much less frequently and rely on incoming notifications to refresh counts.
    const baseInterval = connected ? 300_000 : 30_000; // 5min if connected, else 30s
    // add small random jitter to avoid thundering on startup
    const initialDelay = Math.floor(Math.random() * 5000);
    const timer = setTimeout(() => {
      if (!mountedRef.current) return;
      intervalRef.current = setInterval(() => { fetchAssignedSummary(); }, baseInterval);
    }, initialDelay);
    return () => { mountedRef.current = false; clearTimeout(timer); if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; } };
  }, [connected]);

  // Refresh counts immediately when an assignment-related notification arrives
  useEffect(() => {
    if (!notifications || notifications.length === 0) return;
    const latest = notifications[0];
    if (!latest || !latest.type) return;
    const interesting = ['assignment', 'assignment_removed', 'status', 'payment', 'booking'];
    if (interesting.includes(latest.type)) {
      // small debounce to avoid thundering updates
      const t = setTimeout(() => { fetchAssignedSummary(); }, 300);
      return () => clearTimeout(t);
    }
  }, [notifications]);

  return (
    <AppBar data-staff-header="true" position="static" color="inherit" elevation={2} sx={{ mb: 2, borderRadius: 2, boxShadow: 3 }}>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: 72 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img src={logoSrc} alt={platformName} style={{ height: 44, width: 44, objectFit: 'contain', marginRight: 12, borderRadius: 4, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: 1, color: 'primary.main' }}>
              {platformName}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">{routeTitle(location.pathname)} | Staff Panel{displayName ? ` — ${displayName}` : ''}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ bgcolor: 'background.paper', px: 1, borderRadius: 1, display: 'flex', alignItems: 'center', minWidth: { xs: 100, md: 200 } }}>
            <SearchIcon color="action" />
            <InputBase placeholder="Search…" sx={{ ml: 1, flex: 1 }} />
          </Box>
          <Tooltip title={assignedSummary.assignedActive ? `${assignedSummary.assignedActive} active bookings` : 'No active bookings'}>
            <IconButton color="inherit" onClick={() => navigate('/staff/bookings')} aria-label="bookings">
              <Badge badgeContent={assignedSummary.assignedActive} color="primary">
                <EventNoteIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <NotificationDropdown />
          <ProfileDropdown />
          <ThemeToggle />
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default StaffHeader;
